/**
 * Recorrido — el overlay que enseña dónde está cada cosa.
 *
 * Pattern: DESIGN_SYSTEM.md → Spotlight Pattern (aprobado 2026-09-11).
 *
 * ── Por qué un box-shadow y no cuatro paneles ni un <dialog> ─────────────────
 *
 * Eduardo decidió que el velo NO bloquea el clic: "un tutorial que te atrapa se
 * odia en el segundo paso". Eso descarta `<dialog>` (captura el foco y el
 * puntero por definición) y hace innecesarios los cuatro paneles.
 *
 * Queda un solo div del tamaño del ancla, con `box-shadow: 0 0 0 100vmax` del
 * color del velo: pinta todo lo de afuera y no dibuja nada adentro. Con
 * `pointer-events: none` el puntero lo atraviesa entero, así que se puede
 * seguir trabajando con el recorrido abierto. Solo la tarjeta recibe clics.
 *
 * Como efecto lateral, el recorte se anima solo: mover un div con `transform` y
 * cambiar su tamaño es una transición, mientras que cuatro paneles habría que
 * coreografiarlos.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button.js';
import { IconButton } from './IconButton.js';
import { Icon } from '../Icon.js';

export interface PasoVisible {
  ancla: string;
  titulo: string;
  texto: string;
}

interface Props {
  pasos: PasoVisible[];
  abierto: boolean;
  /** Se llama al salir, sea por Escape, por la X o por Saltar. */
  onCerrar: () => void;
  /** Se llama solo cuando se llegó al final. Separado de onCerrar a propósito:
   *  "lo vio entero" y "se fue" no son lo mismo para quien lo escribió. */
  onTerminar: () => void;
}

interface Rect { top: number; left: number; width: number; height: number }

function medir(ancla: string): Rect | null {
  const el = document.querySelector(`[data-tour-id="${ancla}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  // Un elemento con 0×0 existe en el DOM pero no en la pantalla: resaltarlo
  // sería un recorte invisible en una esquina.
  if (r.width === 0 || r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

const PAD = 8; // se lee del token en el style; esto es solo para el cálculo de la tarjeta

export function Recorrido({ pasos, abierto, onCerrar, onTerminar }: Props) {
  const { t } = useTranslation();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const tarjetaRef = useRef<HTMLDivElement>(null);

  const paso = pasos[i];

  // Volver a empezar cada vez que se abre: un recorrido que retoma donde
  // quedó la vez pasada confunde más de lo que ayuda.
  useEffect(() => { if (abierto) setI(0); }, [abierto]);

  const recalcular = useCallback(() => {
    if (!abierto || !paso) return;
    setRect(medir(paso.ancla));
  }, [abierto, paso]);

  // useLayoutEffect y no useEffect: medir después del pintado produce un
  // fotograma con el recorte en la posición anterior, y se ve como un salto.
  useLayoutEffect(recalcular, [recalcular]);

  useEffect(() => {
    if (!abierto) return;
    window.addEventListener('resize', recalcular);
    // `true` para capturar el scroll de cualquier contenedor, no solo el de la
    // ventana: las listas de esta app scrollean por dentro.
    window.addEventListener('scroll', recalcular, true);
    return () => {
      window.removeEventListener('resize', recalcular);
      window.removeEventListener('scroll', recalcular, true);
    };
  }, [abierto, recalcular]);

  // Un paso cuyo ancla no está en la pantalla se salta solo, sin dejar el velo
  // señalando la nada. Si no queda ninguno, el recorrido se cierra.
  //
  // Pero antes de darlo por ausente se reintenta en el frame siguiente: al
  // abrirse, un elemento puede estar montado y todavía sin medida (una lista
  // que acaba de recibir sus filas, por ejemplo). Sin este reintento se pierde
  // el primer paso, que es el único que todos ven — medido en producción el
  // 2026-09-11: en Contactos salía el tercero.
  useEffect(() => {
    if (!abierto || !paso) return;
    if (rect !== null) return;
    const id = requestAnimationFrame(() => {
      const segundoIntento = medir(paso.ancla);
      if (segundoIntento) { setRect(segundoIntento); return; }
      if (i + 1 < pasos.length) setI(i + 1);
      else onCerrar();
    });
    return () => { cancelAnimationFrame(id); };
  }, [abierto, paso, rect, i, pasos.length, onCerrar]);

  useEffect(() => {
    if (!abierto) return;
    const onKey = (e: KeyboardEvent) => {
      // Salir no se pregunta.
      if (e.key === 'Escape') { onCerrar(); return; }
      if (e.key === 'ArrowRight') setI(n => Math.min(n + 1, pasos.length - 1));
      if (e.key === 'ArrowLeft') setI(n => Math.max(n - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [abierto, onCerrar, pasos.length]);

  // El foco viaja con el paso: sin esto, el recorrido solo existe para quien
  // usa mouse.
  useEffect(() => {
    if (abierto && rect) tarjetaRef.current?.focus();
  }, [abierto, rect, i]);

  if (!abierto || !paso || !rect) return null;

  const ultimo = i === pasos.length - 1;
  const vh = window.innerHeight;
  const debajo = rect.top + rect.height + 12;
  // La tarjeta va debajo del área, salvo que no entre: ahí va arriba. Nunca
  // encima del área — taparía justo lo que se está señalando.
  const cabeDebajo = debajo + 180 < vh;

  return (
    <div
      // El contenedor no captura nada: es el marco de referencia de las
      // posiciones, y el velo tiene que dejar pasar el puntero.
      style={{ position: 'fixed', inset: 0, zIndex: 60, pointerEvents: 'none' }}
      data-evo-recorrido
    >
      {/* El recorte. Todo el velo es su sombra. */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
          borderRadius: 'var(--evo-spotlight-r)',
          boxShadow: '0 0 0 100vmax var(--evo-spotlight-veil)',
          pointerEvents: 'none',
          transitionProperty: 'top, left, width, height',
          transitionDuration: 'var(--evo-motion-spotlight)',
        }}
      />

      <div
        ref={tarjetaRef}
        role="dialog"
        // No es modal y no lo finge: se puede seguir usando la app detrás.
        aria-modal={false}
        aria-label={paso.titulo}
        tabIndex={-1}
        style={{
          position: 'fixed',
          top: cabeDebajo ? debajo : Math.max(12, rect.top - 12 - 180),
          // clamp y no un cálculo en JS: el ancho de la tarjeta es un token, y
          // leerlo desde JS para restarlo obligaría a duplicar su valor acá.
          left: `clamp(12px, ${String(Math.round(rect.left))}px, calc(100vw - var(--evo-spotlight-card-w) - 12px))`,
          width: 'min(var(--evo-spotlight-card-w), calc(100vw - 24px))',
          background: 'var(--evo-bg-2)',
          border: '1px solid var(--evo-stroke)',
          borderRadius: 'var(--evo-r)',
          padding: 'var(--evo-space-4)',
          boxShadow: 'var(--evo-shadow-2)',
          pointerEvents: 'auto',
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--evo-space-3)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-2)' }}>
          <h2 style={{
            margin: 0, flex: 1,
            fontSize: 'var(--evo-list-title-fs)',
            fontWeight: 'var(--evo-fw-medium)',
            color: 'var(--evo-fg-1)',
            fontFamily: 'var(--evo-font)',
          }}>{paso.titulo}</h2>
          <IconButton
            icon={<Icon name="close" size={14} />}
            ariaLabel={t('common.close')}
            variant="ghost"
            size="sm"
            onClick={onCerrar}
          />
        </div>

        <p style={{
          margin: 0,
          fontSize: 'var(--evo-fs-label)',
          color: 'var(--evo-fg-3)',
          fontFamily: 'var(--evo-font)',
          lineHeight: 1.5,
        }}>{paso.texto}</p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
          <span style={{
            flex: 1,
            fontSize: 'var(--evo-fs-tiny)',
            color: 'var(--evo-fg-4)',
            fontFamily: 'var(--evo-font)',
          }}>
            {t('recorrido.contador', { actual: i + 1, total: pasos.length })}
          </span>
          {!ultimo && (
            <Button variant="ghost" size="sm" onClick={onCerrar}>{t('recorrido.saltar')}</Button>
          )}
          <Button
            variant="solid"
            tone="cyan"
            size="sm"
            onClick={() => { if (ultimo) { onTerminar(); } else { setI(i + 1); } }}
          >
            {ultimo ? t('recorrido.terminar') : t('recorrido.siguiente')}
          </Button>
        </div>
      </div>
    </div>
  );
}
